import { getAnswer } from "./runtime";
import type {
  QuestionnaireAnswers,
  QuestionnaireCondition,
  QuestionnaireDefinition,
} from "./schema";

/** Accept both stored intake fields and their questionnaire projection. */
type ContactPolicyField = {
  id: string;
  kind: string;
  fieldKey?: string;
  binding?: string;
  bindingKey?: string | null;
  analyticsKey?: string;
  source?: { reference?: string };
  options?: unknown;
  allowMultiple?: boolean;
};

export function intakeContactPreferenceField(
  fields: ContactPolicyField[],
): ContactPolicyField | undefined {
  return fields.find(
    (field) =>
      (field.fieldKey === "contact_preference" ||
        field.analyticsKey === "intake.contact_preference") &&
      (field.kind === "enum" ||
        (field.kind === "multiple_choice" && !field.allowMultiple)) &&
      Array.isArray(field.options) &&
      field.options.some(
        (option: unknown) =>
          option !== null &&
          typeof option === "object" &&
          "id" in option &&
          option.id === "email",
      ),
  );
}

export function isIntakePhoneField(field: ContactPolicyField): boolean {
  return (
    field.fieldKey === "phone" ||
    field.analyticsKey === "intake.phone" ||
    field.bindingKey === "contact.phone" ||
    field.source?.reference === "contact.phone"
  );
}

export function intakePrefersEmail(
  fields: ContactPolicyField[],
  answers: QuestionnaireAnswers,
): boolean {
  const preference = intakeContactPreferenceField(fields);
  if (!preference) return false;
  return (
    (getAnswer(answers, preference.binding ?? preference.id) ??
      getAnswer(answers, "contact_preference")) === "email"
  );
}

/** Hidden values must not reach validation, submissions, or CRM updates. */
export function omitIntakePhoneAnswers(
  fields: ContactPolicyField[],
  answers: QuestionnaireAnswers,
): QuestionnaireAnswers {
  const preference = intakeContactPreferenceField(fields);
  const result = { ...answers };
  if (preference) {
    const binding = preference.binding ?? preference.id;
    const value =
      getAnswer(answers, binding) ?? getAnswer(answers, "contact_preference");
    if (value !== undefined) result[binding] = value;
  }
  if (!intakePrefersEmail(fields, result)) return result;
  for (const field of fields.filter(isIntakePhoneField)) {
    delete result[field.id];
    if (field.binding) delete result[field.binding];
    if (field.fieldKey) delete result[field.fieldKey];
  }
  delete result.phone;
  delete result["contact.phone"];
  return result;
}

/** Idempotent compatibility policy for new and already-pinned definitions. */
export function applyIntakeContactPolicy(
  definition: QuestionnaireDefinition,
): QuestionnaireDefinition {
  const fields = definition.steps.flatMap((step) => step.fields);
  const preference = intakeContactPreferenceField(fields);
  if (!preference) return definition;
  const phoneVisible: QuestionnaireCondition = {
    op: "not",
    condition: {
      op: "equals",
      field: preference.binding ?? preference.id,
      value: "email",
    },
  };
  const steps = definition.steps.map((step) => ({
    ...step,
    visibleWhen:
      step.fields.length > 0 && step.fields.every(isIntakePhoneField)
        ? andCondition(step.visibleWhen, phoneVisible)
        : step.visibleWhen,
    fields: step.fields.map((field) =>
      isIntakePhoneField(field)
        ? {
            ...field,
            required: true,
            visibleWhen: andCondition(field.visibleWhen, phoneVisible),
          }
        : field,
    ),
  }));
  const preferenceIndex = steps.findIndex((step) =>
    step.fields.some((field) => field.id === preference.id),
  );
  const contactIndex = steps.findIndex((step) =>
    step.fields.some(
      (field) =>
        isIntakePhoneField(field) ||
        field.analyticsKey === "intake.full_name" ||
        field.analyticsKey === "intake.email" ||
        field.source?.reference?.startsWith("contact."),
    ),
  );
  if (contactIndex >= 0 && preferenceIndex > contactIndex) {
    const [preferenceStep] = steps.splice(preferenceIndex, 1);
    if (preferenceStep) steps.splice(contactIndex, 0, preferenceStep);
  }
  return {
    ...definition,
    initialStepId:
      definition.initialStepId === definition.steps[0]?.id
        ? steps[0]?.id
        : definition.initialStepId,
    steps,
  };
}

function andCondition(
  existing: QuestionnaireCondition | undefined,
  condition: QuestionnaireCondition,
): QuestionnaireCondition {
  if (!existing) return condition;
  const serialized = JSON.stringify(condition);
  if (
    JSON.stringify(existing) === serialized ||
    (existing.op === "and" &&
      existing.conditions.some((item) => JSON.stringify(item) === serialized))
  )
    return existing;
  return { op: "and", conditions: [existing, condition] };
}
